import { sitePathname } from "../sitePath";
// /app/javascripts/components/RenderContent.jsx
// noinspection RegExpRedundantEscape

// Component to Display Section Records

import React, { useLayoutEffect, useRef } from "react";
import DisplayContent from "./DisplayContent";
import RenderCell from "./RenderCell.jsx";
import { isTextOnly } from "./getDefaultOptions";
import { dupObject, isPresent } from "./utilities";
import PropTypes from "prop-types";

import {
  handleImageGroup,
  handleImageArray,
  processVideoImages,
  processVideoImageTag,
  imageFileFindByName,
  missingImageUrl,
} from "./imageProcessingUtilities.jsx";

/**
 * @param {{ section?: import('../types/dataTypes').Section | null,
 * editing?: boolean, noBorder?: boolean, noHidden?: boolean,
 * onChange?: ((cell: import('../types/dataTypes').Cell, action: string) => void) | null }} props
 */
const RenderSection = ({
  section = null,
  editing = false,
  noBorder = true,
  noHidden = false,
  onChange = null,
  actionsTargetId = null,
}) => {
  if (section === null) return null; // We can't render what we don't have

  const sectionData = dupObject(section);
  if (!sitePathname().startsWith("/admin/")) {
    if (sectionData.enabled === false) return null;
    if (sectionData.cells) sectionData.cells = sectionData.cells.filter(cell => cell.enabled !== false);
  }

  if (sectionData.cells?.length > 0) {
    return processCells(sectionData.cells, editing, noBorder, noHidden, onChange, actionsTargetId);
  } else {
    const contents = buildContents(sectionData);
    const sections = [];

    processVideoImages(contents);

    contents.forEach((content) => {
      sections.push(renderSection(content, noBorder, noHidden, onChange));
    });

    return sections;
  }
};

// Utility Functions

function renderSection(content, noBorder = false, noHidden, onChange = null) {
  let divClass = "w-100 border border-danger border-width-8";

  if (noBorder) divClass = "w-100 m-0 p-3";

  return (
    <div className="row mb-2">
      <div id="sectionAttributes" className={divClass}>
        <DisplayContent
          content={content.description}
          image={content.image}
          link={content.link}
          format={content.formatting}
          sectionId={content.sectionName}
          textAttributes={content.text_attributes}
          imageAttributes={content.image_attributes}
          noHidden={noHidden}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

function buildContents(section) {
  const sections = processSection(section);

  return sections;
}

// Mark only columns that have a neighbour to their left, including after resizing.
function CellRow({ editing, className, id, children, actionsTargetId }) {
  const row = useRef(null);
  useLayoutEffect(() => {
    if (!editing) return;
    const element = row.current;
    const updateDividers = () => {
      const columns = Array.from(element.children);
      columns.forEach((column, index) => {
        const previous = columns[index - 1];
        column.classList.toggle("admin-cell-divider", !!previous && column.offsetLeft > previous.offsetLeft);
      });
      const actions = actionsTargetId && document.getElementById(actionsTargetId);
      if (actions) {
        const toolbar = actions.parentElement;
        const bounds = element.getBoundingClientRect();
        actions.style.width = `${bounds.width}px`;
        actions.style.marginLeft = `${bounds.left - toolbar.getBoundingClientRect().left}px`;
        actions.style.gridTemplateColumns = columns.map(column => `${column.getBoundingClientRect().width}px`).join(" ");
        Array.from(actions.children).forEach((control, index) => {
          if (columns[index]) control.style.paddingLeft = getComputedStyle(columns[index]).paddingLeft;
        });
        const sectionButtons = toolbar.querySelector(".admin-section-buttons");
        const lastControl = actions.lastElementChild?.lastElementChild;
        if (sectionButtons && lastControl) {
          const availableLeft = toolbar.getBoundingClientRect().right - sectionButtons.getBoundingClientRect().width;
          toolbar.classList.toggle("admin-actions-wrap", lastControl.getBoundingClientRect().right + 16 > availableLeft);
        }
      }
    };
    const observer = new ResizeObserver(updateDividers);
    observer.observe(element);
    updateDividers();
    return () => observer.disconnect();
  }, [editing, children, actionsTargetId]);
  return <div ref={row} className={className} id={id}>{children}</div>;
}

const processCells = (
  cells,
  editing = false,
  noBorder = false,
  noHidden = true,
  onChange = null,
  actionsTargetId = null,
) => {
  if (!cells || cells.length === 0) return null;

  const rowClasses = new Set(["row"]);
  const alignments = new Set();
  let containerId = "";

  cells.forEach((cell) => {
    containerId = cell.section_name;

    if (cell.formatting) {
      const cellContainerClasses = cell.formatting["container_classes"];

      // A shared row has one alignment per breakpoint. Use the first column's
      // setting consistently in public rendering and the admin preview instead
      // of letting Bootstrap's stylesheet order choose a conflicting value.
      for (const token of (cellContainerClasses || "").split(/\s+/).filter(Boolean)) {
        const alignment = token.match(/^align-items-(?:(sm|md|lg|xl|xxl)-)?(start|center|end|baseline|stretch)$/);
        if (alignment) {
          const breakpoint = alignment[1] || "base";
          if (alignments.has(breakpoint)) continue;
          alignments.add(breakpoint);
        }
        rowClasses.add(token);
      }
    }

    processCell(cell);
  });

  const containerClasses = [...rowClasses].join(" ");
  const alignment = containerClasses.match(
    /(?:^|\s)align-items-(start|center|end|baseline|stretch)(?:\s|$)/,
  )?.[1];
  const contentAlignment =
    alignment === "start" ? "flex-start" : alignment === "end" ? "flex-end" : alignment;

  return (
    <CellRow editing={editing} actionsTargetId={actionsTargetId} className={`${containerClasses}${editing ? " admin-section-cells" : ""}`} id={containerId}>
      {cells.map((cell, index) => (
        <div
          key={index}
          className={`content-section-cell ${cell.container_class || ""}${editing ? " admin-section-cell" : ""}`}
          style={{
            ...(editing ? { alignSelf: "stretch", display: "flex", flexDirection: "column" } : {}),
            ...(cell.width === "auto"
              ? { flex: 1 }
              : { width: `${cell.width}` }),
          }}
        >
          <RenderCell
            cell={cell}
            actionsTargetId={actionsTargetId}
            editing={editing}
            noBorder={noBorder}
            noHidden={noHidden}
            onChange={onChange}
            contentAlignment={editing ? contentAlignment : undefined}
          />
        </div>
      ))}
    </CellRow>
  );
};

function processCell(cell) {
  if (!cell) return;

  if (cell.content) {
    const match = cell.content.match(/VideoImage:\s*"(.+)"/);

    if (match) cell.content = processVideoImageTag(cell.content, match[1]);
  }

  if (cell.formatting && cell.formatting["classes"]) {
    let classes = cell.formatting["classes"];
    const match = classes.match(/col(?:-(xs|sm|md|lg|xl|xxl))?-(\d{1,2})/);

    if (match) {
      cell.container_class = match[0];
      // Remove the matched string from the classes field
      cell.formatting["classes"] = classes.replace(match[0], "").trim();
    }
  }
}

function processSection(section) {
  const rowStyle = isPresent(section.row_style)
    ? section.row_style
    : section.formatting.row_style;

  if (isTextOnly(rowStyle) || !section.image)
    // Nothing to do
    return [section];

  const imageGroupRegex = /^\s*ImageGroup:\s*(.+)\s*$/;
  const videoRegex = /^\s*VideoImage:"\s*(.+)\s*"$/;
  const imageFileRegex = /^\s*ImageFile:\s*(.+)\s*$/;
  const imageSectionRegex = /^\s*ImageSection:\s*(.+)\s*$/;
  const imageArrayRegex = /^\s*\[\s*(.+?)\s*\]\s*$/m;

  let isImageSection = false;
  let newImages = section.image.slice().trim();
  let newDescription = section.description;
  let newFormatting = dupObject(section.formatting);
  let subsection = null;
  let match;

  switch (true) {
    case imageGroupRegex.test(newImages):
      match = newImages.match(imageGroupRegex);
      [newImages, newFormatting] = handleImageGroup(
        match[1],
        section.formatting,
      );
      break;
    case videoRegex.test(newImages):
      match = newImages.match(videoRegex);
      newImages = handleVideoFile(section, match[1]);
      break;
    case imageFileRegex.test(newImages):
      match = newImages.match(imageFileRegex);
      newImages = handleSingleImageFile(section, match[1]);
      break;
    case imageSectionRegex.test(newImages):
      isImageSection = true;
      match = newImages.match(imageSectionRegex);
      [newImages, newDescription, subsection, newFormatting] =
        handleImageSection(section, match[1], section.formatting);
      break;
    case imageArrayRegex.test(newImages):
      match = newImages.match(imageArrayRegex);
      [newImages, newFormatting] = handleImageArray(
        match[1],
        section.formatting,
      );
      break;
    default:
      newImages = typeof newImages === "string" ? newImages : newImages.image_url;
  }

  if (isImageSection && isPresent(subsection)) {
    section.image = newImages;
    section.description = newDescription;
    section.formatting = newFormatting;

    return [section, subsection];
  } else {
    section.image = newImages;
    section.description = newDescription;
    section.formatting = newFormatting;

    return [section];
  }
}

function handleVideoFile(section, name) {
  const imageFile = imageFileFindByName(name);
  const results = imageFile.image_url;

  section.link = results;

  return results;
}

function handleSingleImageFile(section, name) {
  const imageFile = imageFileFindByName(name);
  const results = imageFile.image_url;

  section.link = results;

  return results;
}

function handleImageSection(section, name, formatting) {
  const imageFile = imageFileFindByName(name);

  if (imageFile.image_url) {
    const caption = imageFile.caption;
    const containsOnlyPTagsOrNoHTML = /^(\s*<p>.*?<\/p>\s*)*$/i.test(caption);
    let description;

    if (containsOnlyPTagsOrNoHTML)
      description = `<div class='display-4 fw-bold mb-1 text-dark'>${caption}</div>`;
    else description = caption;

    section.link = imageFile.image_url;
    const [subsection, updatedFormatting] = buildSubsection(
      section,
      imageFile,
      formatting,
    );

    return [imageFile.image_url, description, subsection, updatedFormatting];
  } else {
    return [missingImageUrl(), "", null, null];
  }
}

function buildSubsection(section, imageFile, formatting) {
  const subsection = JSON.parse(JSON.stringify(section));

  subsection.link = null;
  subsection.image = null;
  subsection.formatting = flipFormattingSide(formatting);
  subsection.description = imageFile.description;

  if (formatting && "expanding_rows" in formatting)
    delete formatting.expanding_rows;

  return [subsection, formatting];
}

function swapClasses(formatting) {
  if (formatting.text_classes && formatting.image_classes) {
    formatting.image_classes = formatting.image_classes.replace(
      /w-\d{2,3}/g,
      "",
    );

    const temp = formatting.image_classes;
    formatting.image_classes = formatting.text_classes;
    formatting.text_classes = temp;
  }

  if (formatting.row_classes) {
    formatting.row_classes = formatting.row_classes.replace(/mt-\d|pt-\d/g, "");
  }

  return formatting;
}

function flipFormattingSide(formatting) {
  let newFormatting = JSON.parse(JSON.stringify(formatting));

  if (!formatting || Object.keys(formatting).length === 0) {
    return { row_style: "text-right" };
  }

  if (newFormatting.row_style === "text-left") {
    newFormatting.row_style = "text-right";
    swapClasses(newFormatting);
  } else {
    newFormatting.row_style = "text-left";
    swapClasses(newFormatting);
  }

  return newFormatting;
}

RenderSection.propTypes = {
  section: PropTypes.shape({
    content_type: PropTypes.string,
    section_name: PropTypes.string,
    section_order: PropTypes.number,
    image: PropTypes.string,
    link: PropTypes.string,
    formatting: PropTypes.any,
    description: PropTypes.string,
    row_style: PropTypes.string,
    text_margin_top: PropTypes.string,
    text_margin_left: PropTypes.string,
    text_margin_right: PropTypes.string,
    text_margin_bottom: PropTypes.string,
    text_background_color: PropTypes.string,
    image_margin_top: PropTypes.string,
    image_margin_left: PropTypes.string,
    image_margin_right: PropTypes.string,
    image_margin_bottom: PropTypes.string,
    image_background_color: PropTypes.string,
  }).isRequired, // Use `.isRequired` here,
  editing: PropTypes.bool,
  noBorder: PropTypes.bool,
  noHidden: PropTypes.bool,
  onChange: PropTypes.any,
};

export default RenderSection;
